import React, { useEffect, useState } from "react";
import {
  Stack,
  Text,
  Button,
  Alert,
  Group,
  NumberInput,
  Center,
  Loader,
} from "@mantine/core";
import { IconGift, IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../../../../context/AppContext";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { GiftCardConfig } from "../../../../../types/giftCards";
import GiftCardCodeField from "../../../../common/GiftCardCodeField";

interface GiftCardLineItemFormProps {
  /** Salon UUID (businessInfo.id) — routed to the gift-card config + code check. */
  salonId: string;
  /** Emits the add-line-item payload: `{ gift_cards: [{ amount, code? }] }`. */
  onSubmit: (lineItemData: {
    gift_cards: Array<{ amount: number; code?: string }>;
  }) => void;
  onCancel: () => void;
}

/**
 * Sell a gift card as a LINE ITEM on an existing transaction. Collects the face
 * value (validated against the salon's min/max) + an optional staff-typed custom
 * code, then emits a `{ gift_cards: [...] }` payload for the shared
 * add-line-item endpoint. Tax-exempt and commission-exempt on the backend; the
 * card is minted once the whole transaction is paid.
 *
 * Mirrors the standalone GiftCardSaleModal's inner form (config load + amount +
 * code field), but adds to the current ticket instead of creating its own sale.
 */
export default function GiftCardLineItemForm({
  salonId,
  onSubmit,
  onCancel,
}: GiftCardLineItemFormProps) {
  const { getGiftCardConfig } = useApp();

  const [config, setConfig] = useState<GiftCardConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [amount, setAmount] = useState<number | "">("");
  const [code, setCode] = useState("");
  const [codeValid, setCodeValid] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!salonId) return;
    let cancelled = false;
    (async () => {
      setIsLoadingConfig(true);
      try {
        const cfg = await getGiftCardConfig(salonId);
        if (!cancelled) setConfig(cfg);
      } catch (err) {
        console.error("Failed to load gift card config:", err);
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to load gift card settings. Please try again.",
          );
        }
      } finally {
        if (!cancelled) setIsLoadingConfig(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salonId, getGiftCardConfig]);

  const amountIsNumber = typeof amount === "number";
  const min = config?.min_amount ?? 0;
  const max = config?.max_amount ?? 0;
  const enabled = config?.is_enabled ?? false;

  const validationError: string | null = !amountIsNumber
    ? null
    : amount < min
      ? `Enter an amount of at least ${formatCurrency(min)}.`
      : max > 0 && amount > max
        ? `Enter an amount of at most ${formatCurrency(max)}.`
        : null;

  const canSubmit =
    enabled &&
    amountIsNumber &&
    amount >= min &&
    (max <= 0 || amount <= max) &&
    !validationError &&
    codeValid &&
    !isLoadingConfig;

  const handleSubmit = () => {
    if (!canSubmit || !amountIsNumber) return;
    onSubmit({ gift_cards: [{ amount, code: code.trim() || undefined }] });
  };

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      {isLoadingConfig ? (
        <Center py="xl">
          <Loader color="var(--brand-teal)" />
        </Center>
      ) : !enabled && !error ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="yellow"
          variant="light"
        >
          Gift cards aren't enabled for this location yet. Turn them on in
          Settings → Gift Cards to sell one.
        </Alert>
      ) : (
        enabled && (
          <>
            <Text size="sm" c="dimmed">
              The gift card is added to this ticket and charged at checkout. The
              card is created once the sale is paid.
            </Text>
            <NumberInput
              label="Amount"
              placeholder="e.g. 50.00"
              value={amount}
              onChange={(value) =>
                setAmount(typeof value === "number" ? value : "")
              }
              min={0}
              decimalScale={2}
              prefix="$"
              withAsterisk
              error={validationError}
              description={
                max > 0
                  ? `Between ${formatCurrency(min)} and ${formatCurrency(max)}.`
                  : `At least ${formatCurrency(min)}.`
              }
            />
            <GiftCardCodeField
              salonId={salonId}
              value={code}
              onChange={setCode}
              onValidityChange={setCodeValid}
            />
          </>
        )
      )}

      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="var(--brand-teal)"
          onClick={handleSubmit}
          disabled={!canSubmit}
          leftSection={<IconGift size={18} />}
        >
          Add Gift Card
        </Button>
      </Group>
    </Stack>
  );
}
